import { getAnalytics } from "@tm/insurance/analytics";
import { FlexComp, H2Tag, H5Tag } from "@tm/static";
import { useBreakpointV2 } from "@tm/ui-widgets";
import {
  IntersectionObserverData,
  useIntersectionObserver,
} from "@tm/ui/hooks";
import { theme } from "@tm/utility/global";
import React, { useCallback, useRef } from "react";
import { InnerContentWrap } from "../../CommonStyle/CommonStyles.style";
import FormattedText, { TextNodeType } from "../FormattedText/FormattedText";
import ClaimStructure from "./ClaimStructure";
import { ClaimYourInsuranceStyle } from "./ClaimYourInsurance.style";

interface ClaimYourInsuranceProps {
  data?: {
    claimInfo: TextNodeType | string;
    claimIndex?: string;
  }[];
  title?: TextNodeType;
  info?: TextNodeType;
}

const ClaimYourInsurance = ({ data, title, info }: ClaimYourInsuranceProps) => {
  const device = useBreakpointV2();
  const analytics = getAnalytics();
  const hasTracked = useRef<boolean>(false);

  // Track component view when it comes into viewport
  const handleIntersection = useCallback((data: IntersectionObserverData) => {
    if (!hasTracked.current) {
      analytics?.trackLandingEvents({
        actionType: "claim_your_insurance_seen",
      });
    }
    hasTracked.current = true;
  }, []);

  const { ref: claimInsuranceRef } = useIntersectionObserver(
    handleIntersection,
    {
      threshold: 0.5, // Trigger when 50% of component is visible
      triggerOnLeaveViewport: true,
    }
  );

  return (
    <ClaimYourInsuranceStyle ref={claimInsuranceRef}>
      <InnerContentWrap>
        {device == "lg" ? (
          <React.Fragment>
            <H2Tag>{title && <FormattedText value={title} />}</H2Tag>
            <H2Tag color={theme.greenText} margin="0 0 86px">
              {info && <FormattedText value={info} />}
            </H2Tag>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <H5Tag>{title && <FormattedText value={title} />}</H5Tag>
            <H5Tag color={theme.greenText5} margin="0 0 20px">
              {info && <FormattedText value={info} />}
            </H5Tag>
          </React.Fragment>
        )}
        <FlexComp gap={device == "lg" ? "100px" : "48px"}>
          {data &&
            data.map((item, index) => {
              return (
                <React.Fragment key={index}>
                  <ClaimStructure
                    key={index}
                    claimInfo={item.claimInfo}
                    claimIndex={item.claimIndex}
                    showSpiral={index == data.length - 1}
                  />
                </React.Fragment>
              );
            })}
        </FlexComp>
      </InnerContentWrap>
    </ClaimYourInsuranceStyle>
  );
};

export default ClaimYourInsurance;
